'use client';

import { ClientLayout } from '@/components/layout/client-layout';
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from '@/context/auth-context';
import { CartProvider } from '@/context/cart-context';
import { OwnedProductsProvider } from '@/context/owned-products-context';
import { WishlistProvider } from '@/context/wishlist-context';
import { NotificationsProvider } from '@/context/notifications-context';
import { ThemeProvider } from '@/context/theme-context';
import { ProductProvider } from '@/context/product-context';
import { ProductDesignProvider } from '@/context/product-design-context';
import { FirebaseClientProvider } from '@/firebase';
import { GeoCountryProvider } from '@/context/geo-context';
import { FCMInitializer } from '@/components/fcm-initializer';
import { AnalyticsProvider } from '@/components/analytics-provider';
import { InviteLinkCapture } from '@/components/invite-link-capture';
import { SellerSourceTracker } from '@/components/seller-source-tracker';

/**
 * NO SUSPENSE BOUNDARY AROUND THE APP, deliberately. One stood here from
 * 2026-02-25 to 2026-10-06. It went in to fix a build error, "useSearchParams()
 * should be wrapped in a suspense boundary", which Next only raises while
 * PRERENDERING a page; since the root layout reads headers(), no page is
 * prerendered (the build lists every page as dynamic), so the error cannot
 * recur. Nothing in the tree suspends on its own either: no use(promise), no
 * React.lazy, and next/dynamic brings its own boundary.
 *
 * What the boundary did do was make every page one streamed unit. React's
 * server renderer writes a completed boundary bigger than about 12.8 KB after
 * its fallback and swaps it in by script, and the browser reveals such a swap
 * no sooner than 300 ms after its first paint. With the whole app inside, every
 * page showed its fallback (a full-screen spinner, later nothing at all) before
 * any of itself, and appeared at least 300 ms late. Without it, the page is the
 * document and paints as it arrives.
 *
 * The flip side is that any Suspense boundary left INSIDE a page is now revealed
 * on its own, after that 300 ms, if React streams it separately; inside the app
 * boundary it was revealed together with everything else. So a page must not
 * wrap content it renders on the server in a boundary it does not need.
 * suspense-boundaries.test.ts holds the line on the ones that were removed.
 */
export function Providers({
  children,
  // Resolved from the request IP in RootLayout (server-only; see
  // src/lib/geo-ip.ts). '' when it could not be resolved.
  geoCountry = '',
  // Display-currency rates read in RootLayout (src/lib/display-rates-server.ts),
  // or null when it had none in time; ThemeProvider then fetches its own.
  displayRates = null,
}: {
  children: React.ReactNode;
  geoCountry?: string;
  displayRates?: Record<string, number> | null;
}) {
  return (
    <GeoCountryProvider country={geoCountry}>
    <FirebaseClientProvider>
        <AuthProvider>
          <NotificationsProvider>
            {/* ProductDesignProvider sits ABOVE ThemeProvider, which is the
                opposite of where it started. A product page can override the
                viewer's ACCENT COLOUR while it is open, and the accent is
                applied to document.documentElement in exactly one effect, in
                ThemeProvider. For that effect to be the single writer it has to
                be able to read the override — hence the hoist.

                The alternative, applying the product accent from lower down,
                loses on ordering: React runs child effects before parent ones,
                so a writer under ThemeProvider would set the seller's colour
                and then have ThemeProvider's own effect immediately paint the
                viewer's back over it. Hoisting also makes reverting free —
                when the product page unmounts and clears the override,
                ThemeProvider's effect re-runs and restores the brand violet
                with no restore path of its own to get wrong.

                Safe to hoist: the provider holds one useState and depends on
                nothing below it. */}
            <ProductDesignProvider>
              <ThemeProvider initialRates={displayRates}>
                <ProductProvider>
                  <CartProvider>
                    <OwnedProductsProvider>
                    <WishlistProvider>
                        <FCMInitializer />
                        <InviteLinkCapture />
                        <SellerSourceTracker />
                        <AnalyticsProvider>
                          <ClientLayout>
                            {children}
                          </ClientLayout>
                        </AnalyticsProvider>
                        <Toaster />
                    </WishlistProvider>
                    </OwnedProductsProvider>
                  </CartProvider>
                </ProductProvider>
              </ThemeProvider>
            </ProductDesignProvider>
          </NotificationsProvider>
        </AuthProvider>
    </FirebaseClientProvider>
    </GeoCountryProvider>
  );
}
